import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const POLL_MS = 45000;
const RECENT_UNREAD_MS = 15 * 60 * 1000;

const NotificationBell = () => {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const seenIdsRef = useRef(new Set());
  const bootstrappedRef = useRef(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      // Safe notifications state for active 4 modules (HRM, Finance, Inventory, Admin)
      setItems([]);
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onOpen = async () => {
    setOpen((v) => !v);
  };

  const onRead = async (n) => {
    setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x)));
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  const onReadAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    setUnreadCount(0);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={onOpen}
        className="relative p-1.5 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label="Notifications"
      >
        <BellIcon className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <button type="button" onClick={onReadAll} className="text-xs text-indigo-600 hover:text-indigo-800">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {!items.length && (
              <p className="px-3 py-6 text-sm text-gray-500 text-center">No notifications</p>
            )}
            {items.map((n) => (
              <div
                key={n._id}
                className={`px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 ${!n.readAt ? 'bg-indigo-50/40' : ''}`}
              >
                <button type="button" className="w-full text-left" onClick={() => onRead(n)}>
                  <div className="text-sm font-medium text-gray-900">{n.title}</div>
                  <div className="text-xs text-gray-600 mt-0.5 line-clamp-3">{n.message}</div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                  </div>
                </button>
                {n.link && (
                  <Link
                    to={n.link}
                    onClick={() => {
                      onRead(n);
                      setOpen(false);
                    }}
                    className="inline-block mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Open
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
